/**
 * Steps 2 and 4 (shared): the dataset, then the evaluators. Prefilled from the
 * agent's last experiment when there is one.
 *
 * A dataset is made here two ways. "Upload CSV" names it, then uploads its file
 * on the import page, which comes back to this step with it picked (the flow's
 * other choices are kept in the tab meanwhile, `wizard-draft.ts`). "Pull from
 * traces" makes a live dataset on a trace source; a live dataset then asks which
 * version this run uses: fresh traces pulled when it starts, or an existing
 * version (`pulls/`).
 */

import { Plus } from "lucide-react";
import { useState } from "react";
import { useLocation } from "react-router";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import { QueryState } from "@/components/ui/states";
import { NewDatasetDialog } from "@/features/datasets/new-dataset-dialog";
import { CreateDrawer } from "@/features/evaluators/create-drawer";
import { EvaluatorPicker } from "../scoring/evaluator-picker";
import { DatasetPicker } from "./dataset-picker";
import { DatasetPreview } from "./dataset-preview";
import { LiveVersionPicker } from "./pulls/live-version-picker";
import { LiveDatasetDialog } from "@/features/datasets/live/live-dataset-dialog";
import type { NewExperiment } from "./use-new-experiment";

export function StepDataset({ flow }: { flow: NewExperiment }) {
  const [creating, setCreating] = useState<"csv" | "traces" | null>(null);
  const here = useLocation();
  return (
    <>
      <DatasetPicker
        datasets={flow.datasets.data}
        datasetId={flow.datasetId}
        onChange={flow.form.setDatasetId}
        onUpload={() => setCreating("csv")}
        onPull={() => setCreating("traces")}
        detail={<DatasetDetail flow={flow} />}
      />
      {creating === "csv" && (
        <NewDatasetDialog
          agentId={flow.target.resolved.agentId}
          returnTo={here.pathname + here.search}
          onClose={() => setCreating(null)}
        />
      )}
      {creating === "traces" && (
        <LiveDatasetDialog
          onClose={() => setCreating(null)}
          onCreated={(d) => (flow.form.setDatasetId(d.id), setCreating(null))}
        />
      )}
    </>
  );
}

/** Beside the dataset list: what the picked dataset holds, or a live one's version to run on. */
function DatasetDetail({ flow }: { flow: NewExperiment }) {
  if (!flow.dataset)
    return <p className="py-10 text-center text-xs text-faint">Pick a dataset to see what's inside.</p>;
  if (!flow.live) return <DatasetPreview dataset={flow.dataset} />;
  return <LiveVersionPicker flow={flow} />;
}

export function StepEvaluators({ flow }: { flow: NewExperiment }) {
  const [creating, setCreating] = useState(false);
  const here = useLocation();
  return (
    <Field
      label="Evaluators"
      aside={
        <span className="flex items-center gap-3">
          {flow.selected.size} selected
          <Button size="sm" onClick={() => setCreating(true)}>
            <Plus size={14} aria-hidden />
            New evaluator
          </Button>
        </span>
      }
    >
      {creating && <CreateDrawer returnTo={here.pathname + here.search} onClose={() => setCreating(false)} />}
      <QueryState query={flow.evaluators} what="evaluators">
        <EvaluatorPicker
          evaluators={flow.evaluators.data ?? []}
          selected={flow.selected}
          onToggle={(id) => flow.form.toggle(id, flow.selected)}
          fix={{
            fixes: flow.fixes,
            paths: flow.paths,
            scope: { datasetId: flow.datasetId, version: flow.version, live: flow.live },
          }}
        />
      </QueryState>
    </Field>
  );
}
